import { Loader2 } from 'lucide-react'
import { useId } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Textarea } from '~/shared/ui/textarea'
import { draftIssue, type MenuDraft, type MenuDraftIssue } from '../lib/menu-editor-model'
import { FullMenuEditor } from './full-menu-editor'
import { LunchMenuEditor } from './lunch-menu-editor'

/** What the editor says above the form: read from files, or nothing found in them. */
export type EditorNotice = 'read' | 'nothing_recognised' | null

const ISSUE_MESSAGES: Record<MenuDraftIssue, () => string> = {
  invalid_price: m.menuEditorIssueInvalidPrice,
  dish_without_name: m.menuEditorIssueDishWithoutName,
  section_without_title: m.menuEditorIssueSectionWithoutTitle,
  no_dishes: m.menuEditorIssueNoDishes,
  invalid: m.menuEditorIssueInvalid,
}

interface MenuEditorProps {
  draft: MenuDraft
  locale: string
  notice: EditorNotice
  publishing: boolean
  onChange: (draft: MenuDraft) => void
  onPublish: () => void
  onDiscard: () => void
}

export function MenuEditor({
  draft,
  locale,
  notice,
  publishing,
  onChange,
  onPublish,
  onDiscard,
}: MenuEditorProps) {
  const id = useId()
  const issue = draftIssue(draft)
  const lunch = draft.kind === 'lunch'

  return (
    <Card className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold font-serif text-2xl text-foreground tracking-tight">
          {lunch ? m.menuEditorLunchHeading() : m.menuEditorFullHeading()}
        </h2>
        {notice && (
          <p
            className={cn(
              'text-sm',
              notice === 'nothing_recognised'
                ? 'text-brand-subtle-foreground'
                : 'text-muted-foreground',
            )}
          >
            {notice === 'read' ? m.menuEditorReadHint() : m.menuEditorNothingRecognised()}
          </p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-title`}>{m.menuEditorTitleLabel()}</Label>
          <Input
            id={`${id}-title`}
            value={draft.title}
            maxLength={80}
            placeholder={lunch ? m.menuLunchTitle() : m.menuFullTitle()}
            onChange={(event) => onChange({ ...draft, title: event.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-note`}>{m.menuEditorNoteLabel()}</Label>
          <Textarea
            id={`${id}-note`}
            value={draft.note}
            maxLength={280}
            rows={2}
            placeholder={
              lunch ? m.menuEditorLunchNotePlaceholder() : m.menuEditorFullNotePlaceholder()
            }
            onChange={(event) => onChange({ ...draft, note: event.target.value })}
          />
        </div>
      </div>

      {draft.kind === 'lunch' ? (
        <LunchMenuEditor draft={draft} locale={locale} onChange={onChange} />
      ) : (
        <FullMenuEditor draft={draft} onChange={onChange} />
      )}

      <div className="flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="min-h-5 text-muted-foreground text-sm" aria-live="polite">
          {issue ? ISSUE_MESSAGES[issue]() : ''}
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" disabled={publishing} onClick={onDiscard}>
            {m.menuEditorDiscard()}
          </Button>
          <Button type="button" disabled={issue !== null || publishing} onClick={onPublish}>
            {publishing && <Loader2 className="animate-spin" />}
            {publishing ? m.menuEditorPublishing() : m.menuEditorPublish()}
          </Button>
        </div>
      </div>
    </Card>
  )
}
